// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import Protected from "./pages/ProtectedPage";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import UploadPage from "./pages/UploadPage";
import UsersPage from "./pages/UsersPage";
import UploadTitlesPage from "./pages/UploadTitlesPage";
import TitleViewTablePage from "./pages/TitleViewTablePage";
import { FaUserCircle } from "react-icons/fa";
import ViewTitleImagesPage from "./pages/ViewTitleImagesPage";
import ViewTitlesJSONPage from "./pages/ViewTitlesJSONPage";
import ViewPolicebookPage from "./pages/ViewPolicebookPage";
import TR9Page from "./pages/TR9Page";
import TitleAgentAdminPage from "./pages/TitleAgentAdminPage";
import ViewNMVTISPage from "./pages/ViewNMVTISPage";
import AdminChatPage from "./pages/AdminChatPage";
import CoworkPage from "./pages/CoworkPage";
import GeneralChatPage from "./pages/GeneralChatPage";
import ResearchChatPage from "./pages/ResearchChatPage";
import AuditLogPage from "./pages/AuditLogPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import EditHolidaysPage from "./pages/EditHolidaysPage";
import AdminPage from "./pages/AdminPage";
import ModulesPage from "./pages/ModulesPage";
import CaptureDevicesPage from "./pages/CaptureDevicesPage";
import InvoiceReviewPage from "./pages/InvoiceReviewPage";
import AccessControlPage from "./pages/AccessControlPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import DashboardsPage from "./pages/DashboardsPage";
import DashboardsAdminPage from "./pages/DashboardsAdminPage";
// Old Navbar intentionally not imported to avoid double bars
import TopBar from "./components/TopBar";
import Sidebar from "./components/Sidebar";
import { AuthContext } from "./context/AuthContext";
import { installAuthGuard } from "./authGuard";

import "bootstrap/dist/css/bootstrap.min.css";  // keep Bootstrap first
import "./App.css";                             // theme last so it overrides

// Bounce to login on any authentication failure, app-wide.
installAuthGuard();

/* =========================
   JWT & Auth Utilities
   ========================= */

// Safely read/write token in localStorage
const getToken = () => localStorage.getItem("token") || "";
const setTokenLS = (t) =>
  t ? localStorage.setItem("token", t) : localStorage.removeItem("token");

// Decode a JWT without external deps (handles URL-safe Base64)
function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// Many providers put role in different claims; normalize to a single string
function extractRoleFromClaims(claims) {
  if (!claims) return "";
  const r1 = claims.role;
  const r2 = Array.isArray(claims.roles) && claims.roles.length ? claims.roles[0] : "";
  const r3 =
    (Array.isArray(claims["https://roles"]) && claims["https://roles"][0]) ||
    claims["https://aegis/role"];
  const flag = claims.is_admin ? "admin" : "";
  const raw = r1 || r2 || r3 || flag || "";
  return String(raw).toLowerCase();
}

// Normalize role from a user object you keep in state
// CTC admin is a tier ABOVE the customer administrator, so it can do everything
// an admin can. Every screen gate goes through this rather than comparing the
// role string, or a new tier silently locks itself out of existing screens --
// which is exactly what happened when ctc_admin was introduced.
function isAdmin(user) {
  return ["admin", "ctc_admin"].includes(getRole(user));
}

function isCtcAdmin(user) {
  return getRole(user) === "ctc_admin";
}

function getRole(user) {
  if (!user) return "";
  const fromUser =
    user.role ||
    (Array.isArray(user.roles) && user.roles[0]) ||
    (user.claims && (user.claims.role || (Array.isArray(user.claims.roles) && user.claims.roles[0]))) ||
    (user.is_admin ? "admin" : "");
  return String(fromUser || "").toLowerCase();
}

// Fetch /api/me and enrich with role from JWT if backend doesn't include it
async function fetchUserFromToken(token) {
  if (!token) return null;
  const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
  const url = API_BASE ? `${API_BASE}/api/me` : "/api/me";
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("not ok");
    const me = await res.json();

    // Add claims + role from JWT if missing
    const claims = parseJwt(token) || {};
    const role = getRole(me) || extractRoleFromClaims(claims);
    const enriched = role ? { ...me, role, claims } : { ...me, claims };

    localStorage.setItem("user", JSON.stringify(enriched));
    return enriched;
  } catch {
    return null;
  }
}

/* =========================
   Auth bootstrap (refresh-safe)
   ========================= */

function useAuthBootstrap() {
  const [token, setToken] = useState(getToken());
  const [user, setUser] = useState(() => {
    // seed from localStorage so role is available on first paint
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
  (async () => {
    const t = getToken();

    // 1) Seed from localStorage so role is available on first paint
    try {
      const u0 = JSON.parse(localStorage.getItem("user") || "null");
      if (u0) setUser(u0);
    } catch {}

    // 2) Allow routes to render right away
    setAuthChecked(true);

    // 3) Verify token + refresh user in the background
    if (!t) {
      setToken("");
      setUser(null);
      localStorage.removeItem("user");
      return;
    }

    const u = await fetchUserFromToken(t);
    if (u) {
      setToken(t);
      setUser(u);
    } else {
      setToken("");
      setUser(null);
      setTokenLS("");
      localStorage.removeItem("user");
    }
  })();
}, []);

  return { token, setToken, user, setUser, authChecked };
}

/* =========================
   Route Guards
   ========================= */

function isTokenLive(token) {
  if (!token) return false;
  const claims = parseJwt(token);
  // No exp claim -> let the server be the judge (the fetch guard will bounce
  // on a 401). An exp in the past -> already dead, do not even render.
  if (!claims || !claims.exp) return true;
  return claims.exp * 1000 > Date.now();
}

function PrivateRoute({ token, authChecked, children }) {
  // wait for auth bootstrap on hard-refresh
  if (!authChecked) return null; // or a small loader
  return isTokenLive(token) ? children : <Navigate to="/login" replace />;
}
/* =========================
   App
   ========================= */

export default function App() {
  const { token, setToken, user, setUser, authChecked } = useAuthBootstrap();

  const onLoginClick = () => {
    window.location.href = `${process.env.PUBLIC_URL || ""}/login`;
  };

  const onLogoutClick = () => {
    setTokenLS("");
    localStorage.removeItem("user");
    setToken("");
    setUser(null);
    // Optional hard redirect:
    // window.location.href = `${process.env.PUBLIC_URL || ""}/login`;
  };

  return (
    <AuthContext.Provider value={{ token, setToken, user, setUser }}>
      <Router basename={process.env.PUBLIC_URL || ""}>
        {/* Compact top bar */}
        <TopBar
          isAuthed={!!token}
          isAdmin={isAdmin(user)}
          userName={user?.email || user?.name || "Admin"}
          onLogin={onLoginClick}
          onLogout={onLogoutClick}
        />

        {/* Sidebar appears only when authenticated */}
        <div className={!!token ? "page-wrap" : ""}>
          {!!token && <Sidebar />}

          <div className="content">
                      <Routes>
              {/* Public login route */}
              <Route
                path="/login"
                element={
                  <LoginPage
                    token={token}
                    setToken={(t) => {
                      setTokenLS(t);
                      setToken(t);
                    }}
                    setUser={(u) => {
                      // Ensure role/claims are persisted if login returns them
                      if (u && !u.role) {
                        // best-effort enrich from existing token if present
                        const claims = parseJwt(getToken()) || {};
                        const role = extractRoleFromClaims(claims);
                        u = role ? { ...u, role, claims } : { ...u, claims };
                      }
                      localStorage.setItem("user", JSON.stringify(u));
                      setUser(u);
                    }}
                  />
                }
              />

              {/* Upload titles */}
              <Route
                path="/titleagent"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <UploadTitlesPage />
                  </PrivateRoute>
                }
              />

              {/* View all titles */}
              <Route
                path="/titleview"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <TitleViewTablePage />
                  </PrivateRoute>
                }
              />

              {/* View title images */}
              <Route
                path="/titles/images"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ViewTitleImagesPage />
                  </PrivateRoute>
                }
              />
	      
	      {/* View Police Book */}
              <Route
                path="/police-book"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ViewPolicebookPage />
                  </PrivateRoute>
                }
              />
	      {/* Generate TR-9 (Scrap Vehicle Inventory) */}
              <Route
                path="/tr9"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <TR9Page />
                  </PrivateRoute>
                }
              />
	      {/* Title Agent settings — admin only (the API enforces this too) */}
              <Route
                path="/titleagent/admin"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <TitleAgentAdminPage />
                    ) : (
                      <Navigate to="/titleagent" replace />
                    )}
                  </PrivateRoute>
                }
              />
	      {/* View NMVTIS */}
              <Route
                path="/nmvtis"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ViewNMVTISPage />
                  </PrivateRoute>
                }
              />

              {/* View normalized JSON */}
              <Route
                path="/titles/normalized"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ViewTitlesJSONPage />
                  </PrivateRoute>
                }
              />

              {/* Users — admin only (the API enforces this too) */}
              <Route
                path="/users"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <UsersPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Holidays — admin only. The sidebar has linked here all along
                  but no route existed, so the link fell through to the
                  catch-all and silently landed on the dashboard. */}
              <Route
                path="/holidays"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <EditHolidaysPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <Dashboard />
                  </PrivateRoute>
                }
              />

	      {/* Ask & Answer. The chatType stays "chat2": it is the stored
	          ChatSession.surface value and the key that picks the n8n
	          workflow, so renaming the URL must not disturb it. */}
	      <Route
                path="/ask_answer"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <AdminChatPage chatType="chat2" />
                  </PrivateRoute>
                }
              />

	      {/* Old URLs. /chat2 was the Ask & Answer route; /chat was an
	          earlier unlinked surface, now retired -- its existing sessions
	          remain in the database but are no longer reachable here. */}
	      <Route path="/chat2" element={<Navigate to="/ask_answer" replace />} />
	      <Route path="/chat"  element={<Navigate to="/ask_answer" replace />} />

	      {/* Cowork — Hermes agent */}
	      <Route
                path="/cowork"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <CoworkPage />
                  </PrivateRoute>
                }
              />

              {/* Administration hub — admin only */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <AdminPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Ask & Answer data access — admin only */}
              <Route
                path="/access-control"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <AccessControlPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Invoice Agent — review and approve parsed invoices.
                  Not admin-gated: reviewing invoices is ordinary AP work. The
                  API gates it on the invoice-agent module grant. */}
              <Route
                path="/invoiceagent"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <InvoiceReviewPage />
                  </PrivateRoute>
                }
              />

              {/* Aegis Capture device enrollment.
                  Route is admin-gated here; the API additionally requires the
                  aegis-capture module, so a non-admin who guesses the URL gets
                  a page that cannot load anything. */}
              <Route
                path="/capture"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <CaptureDevicesPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Module catalog — admin only */}
              <Route
                path="/modules"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isCtcAdmin(user) ? (
                      <ModulesPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Scheduled reports — any authenticated user */}
              <Route
                path="/scheduled-reports"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ScheduledReportsPage />
                  </PrivateRoute>
                }
              />

              {/* Dashboards — licensed modules, so access is decided by grants
                  and enforced by the API, as Aegis Chat is below. Sidebar
                  entries come from /api/me/modules, so these routes only
                  become reachable once their module is activated.

                  /dashboards is the production shape: one page showing each
                  department its own domains. The two scoped pages below are
                  for the demo, where separate links read more clearly. */}
              <Route
                path="/dashboards"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <DashboardsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboards/operations"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <DashboardsPage title="Operations Dashboard" dashboardKey="dashboards-ops" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboards/manufacturing"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <DashboardsPage title="Manufacturing Dashboard" dashboardKey="dashboards-mfg" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboards/admin"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <DashboardsAdminPage />
                  </PrivateRoute>
                }
              />

              {/* Aegis Chat — a licensed module, so access is decided by
                  grants and enforced by the API rather than by role here.
                  The /api/general-chat/* endpoints keep their names. */}
              <Route
                path="/aegis_chat"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <GeneralChatPage />
                  </PrivateRoute>
                }
              />
              <Route path="/general-chat" element={<Navigate to="/aegis_chat" replace />} />

              {/* Research Chat — evaluation surface for the self-hosted
                  (SearXNG) search path. Reached by URL only: it is deliberately
                  NOT registered in the modules catalog, which is what builds the
                  sidebar, so no menu item appears. Access still requires the
                  Aegis Chat module grant, enforced by the API. */}
              <Route
                path="/research_chat"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <ResearchChatPage />
                  </PrivateRoute>
                }
              />

              {/* Departments & module grants — admin only */}
              <Route
                path="/departments"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <DepartmentsPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Compliance audit log — admin only */}
              <Route
                path="/audit"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    {isAdmin(user) ? (
                      <AuditLogPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />

              {/* Optional protected test route */}
              <Route
                path="/protected"
                element={
                  <PrivateRoute token={token} authChecked={authChecked}>
                    <Protected token={token} />
                  </PrivateRoute>
                }
              />

              {/* Default: decide based on token */}
              <Route
                path="/"
                element={
                  token ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Catch-all */}
              <Route
                path="*"
                element={
                  <Navigate to={token ? "/dashboard" : "/login"} replace />
                }
              />
            </Routes>

	  </div>
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

